import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { SealButton, WhatsAppLink } from "@/components/site/ctas";
import { Link } from "@/components/site/link";
import { Reveal } from "@/components/site/motion";
import { getDict, href, type Dictionary, type Lang, type RfqFlow } from "@/content/site";

const FLOWS: RfqFlow[] = ["spare-parts", "custom-manufacturing", "turnkey-project"];
const INCOTERMS = ["EXW", "FOB", "CIF", "CFR", "DAP"];
const LANG_OPTIONS = ["English", "Français", "العربية", "Türkçe"];

export const Route = createFileRoute("/$lang/rfq/$flow")({
  validateSearch: (search: Record<string, unknown>) => ({
    division: typeof search.division === "string" ? search.division : "",
    country: typeof search.country === "string" ? search.country : "",
  }),
  loader: ({ params }) => {
    if (!FLOWS.includes(params.flow as RfqFlow)) throw notFound();
    return { lang: params.lang as Lang, flow: params.flow as RfqFlow };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const t = getDict(loaderData.lang);
    const f = t.rfq.flows.find((x) => x.flow === loaderData.flow);
    return { meta: [{ title: `${f?.title ?? t.rfq.title} · insidermark` }, { name: "description", content: f?.body ?? t.rfq.intro }] };
  },
  component: Page,
});

function Field({
  id,
  label,
  required,
  hint,
  children,
  full,
  t,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  full?: boolean;
  t: Dictionary;
}) {
  return (
    <div className={`im-field${full ? " im-field--full" : ""}`}>
      <label htmlFor={id}>
        {label}
        {required ? <small>{t.rfq.common.required}</small> : null}
      </label>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

function Page() {
  const { lang, flow } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const t = getDict(lang);
  const c = t.rfq.common;
  const meta = t.rfq.flows.find((f) => f.flow === flow)!;
  const [state, setState] = useState<"idle" | "sending" | "error">("idle");
  const [fileName, setFileName] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState("sending");
    const form = new FormData(e.currentTarget);
    form.set("flow", flow);
    form.set("lang", lang);
    try {
      const res = await fetch("/api/rfq", { method: "POST", body: form });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as { reference: string };
      await navigate({ to: href(lang, "/rfq/received") as never, search: { ref: data.reference } as never });
    } catch {
      setState("error");
    }
  }

  return (
    <main>
      <header className="im-page-head">
        <div className="im-wrap">
          <Reveal>
            <Link className="im-mono" to={href(lang, "/rfq")}>
              ← {t.rfq.title}
            </Link>
            <p className="im-eyebrow mt-6">{meta.time}</p>
            <h1 className="im-display mt-3">{meta.title}</h1>
            <p className="im-body-text mt-5">{meta.body}</p>
          </Reveal>
        </div>
      </header>

      <section className="im-section im-section--tight">
        <div className="im-wrap im-two">
          <aside>
            {flow === "spare-parts" ? (
              <div className="grid gap-4">
                <a className="im-template-link" href="/assets/insidermark-rfq-template.xlsx" download>
                  ↓ {t.rfq.spare.template}
                </a>
                <p className="im-mono">{t.rfq.spare.templateHint}</p>
              </div>
            ) : null}
            {flow === "custom-manufacturing" ? <p className="im-mono">{t.rfq.custom.nda}</p> : null}
            <p className="im-mono mt-6">{c.privacy}</p>
          </aside>

          <form className="im-form" onSubmit={onSubmit} encType="multipart/form-data" noValidate={false}>
            <input type="hidden" name="division" value={search.division} />
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="im-form__grid">
              <Field id="company" label={c.company} required t={t}>
                <input id="company" name="company" required autoComplete="organization" />
              </Field>
              <Field id="contact" label={c.contact} required t={t}>
                <input id="contact" name="contact" required autoComplete="name" />
              </Field>
              <Field id="email" label={c.email} required t={t}>
                <input id="email" name="email" type="email" required autoComplete="email" />
              </Field>
              <Field id="phone" label={c.phone} t={t}>
                <input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="+" />
              </Field>
              <Field id="country" label={c.country} required t={t}>
                <input id="country" name="country" required defaultValue={search.country} autoComplete="country-name" />
              </Field>
              <Field id="language" label={c.language} t={t}>
                <select id="language" name="language" defaultValue={lang === "fr" ? "Français" : lang === "tr" ? "Türkçe" : "English"}>
                  {LANG_OPTIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>

              {flow === "spare-parts" ? (
                <>
                  <Field id="brand" label={t.rfq.spare.brand} hint={t.rfq.spare.brandHint} full t={t}>
                    <input id="brand" name="brand" />
                  </Field>
                  <Field id="port" label={c.port} t={t}>
                    <input id="port" name="port" />
                  </Field>
                  <Field id="incoterms" label={c.incoterms} t={t}>
                    <select id="incoterms" name="incoterms" defaultValue="FOB">
                      {INCOTERMS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                </>
              ) : null}

              {flow === "custom-manufacturing" ? (
                <>
                  <Field id="sector" label={t.rfq.custom.sector} t={t}>
                    <input id="sector" name="sector" />
                  </Field>
                  <Field id="quantity" label={t.rfq.custom.quantity} t={t}>
                    <input id="quantity" name="quantity" inputMode="numeric" />
                  </Field>
                  <Field id="material" label={t.rfq.custom.material} full t={t}>
                    <input id="material" name="material" />
                  </Field>
                  <Field id="series" label={t.rfq.custom.series} t={t}>
                    <select id="series" name="series">
                      {t.rfq.custom.seriesOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="incoterms" label={c.incoterms} t={t}>
                    <select id="incoterms" name="incoterms" defaultValue="FOB">
                      {INCOTERMS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                </>
              ) : null}

              {flow === "turnkey-project" ? (
                <>
                  <Field id="plant" label={t.rfq.turnkey.plant} required t={t}>
                    <select id="plant" name="plant" required>
                      {t.rfq.turnkey.plantOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="capacity" label={t.rfq.turnkey.capacity} hint={t.rfq.turnkey.capacityHint} required t={t}>
                    <input id="capacity" name="capacity" required />
                  </Field>
                  <Field id="site" label={t.rfq.turnkey.site} t={t}>
                    <select id="site" name="site">
                      {t.rfq.turnkey.siteOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="budget" label={t.rfq.turnkey.budget} t={t}>
                    <select id="budget" name="budget">
                      {t.rfq.turnkey.budgetOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="timeline" label={t.rfq.turnkey.timeline} t={t}>
                    <select id="timeline" name="timeline">
                      {t.rfq.turnkey.timelineOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="financing" label={t.rfq.turnkey.financing} t={t}>
                    <select id="financing" name="financing">
                      {t.rfq.turnkey.financingOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </Field>
                </>
              ) : null}

              {flow !== "turnkey-project" ? (
                <div className="im-field im-field--full">
                  <label className="im-check">
                    <input type="checkbox" name="consolidation" value="1" defaultChecked />
                    <span>
                      {c.consolidation}
                      <small>{c.consolidationHint}</small>
                    </span>
                  </label>
                </div>
              ) : null}

              <Field id="notes" label={c.notes} full t={t}>
                <textarea id="notes" name="notes" rows={4} />
              </Field>

              <div className="im-field im-field--full">
                <label className="im-drop" htmlFor="file">
                  <strong>
                    {flow === "spare-parts"
                      ? t.rfq.spare.upload
                      : flow === "custom-manufacturing"
                        ? t.rfq.custom.upload
                        : c.file}
                  </strong>
                  <span>{c.fileHint}</span>
                  {fileName ? <b>{fileName}</b> : null}
                  <input
                    id="file"
                    name="file"
                    type="file"
                    accept=".xlsx,.xls,.csv,.pdf,.dwg,.dxf,.step,.stp,.zip"
                    onChange={(e) => setFileName(e.currentTarget.files?.[0]?.name ?? "")}
                  />
                </label>
              </div>
            </div>

            <label className="im-check im-check--consent">
              <input type="checkbox" name="consent" value="1" required />
              <span>
                {c.consent}{" "}
                <Link to={href(lang, "/privacy")} target="_blank">
                  ({c.consentLink})
                </Link>
              </span>
            </label>

            {state === "error" ? <p className="im-form__error">{c.error}</p> : null}

            <div className="im-form__actions">
              <SealButton type="submit" disabled={state === "sending"}>
                {state === "sending" ? c.submitting : c.submit}
              </SealButton>
              <WhatsAppLink label={c.whatsappContinue} text={`${t.hero.waText} (${meta.title})`} />
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
